// CalendarViewsManager.js - Sparade behandlarvyer för dagvyn (Spår K2)
// Klinik-admin skapar/byter namn/tar bort vyer här; alla användare väljer
// dem i dagvyn bredvid "Alla/Ingen".

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import {
  getClinicUsers, getCalendarViews, createCalendarView, updateCalendarView, deleteCalendarView
} from '../Calendar/calendarApi';
import { UserContext } from '../../UserContext';
import './CalendarViewsManager.css';

const EMPTY_FORM = { viewId: null, name: '', userIds: [] };

function CalendarViewsManager() {
  const { user: currentUser } = useContext(UserContext);
  const clinicId = currentUser?.clinic_id;
  const [views, setViews] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null); // null = stängt, annars EMPTY_FORM-format

  const load = useCallback(async () => {
    try {
      const [v, u] = await Promise.all([
        getCalendarViews(),
        getClinicUsers({ calendarOnly: true })
      ]);
      setViews(v);
      // Bara klinikens behandlare med kalender kan ingå i en vy (/api/users
      // ger systemadmin alla klinikers användare - filtrera som kalendervyerna)
      setUsers(u.filter(x => x.has_calendar !== false && x.clinic_id === clinicId));
    } catch (err) {
      console.error('Kunde inte ladda vyer:', err);
      toast.error('Kunde inte ladda sparade vyer');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => { if (clinicId) load(); }, [load, clinicId]);

  const userName = (id) => {
    const u = users.find(x => x.user_id === id);
    return u ? `${u.first_name} ${u.last_name}` : null;
  };

  const toggleUser = (id) => {
    setForm(f => ({
      ...f,
      userIds: f.userIds.includes(id) ? f.userIds.filter(x => x !== id) : [...f.userIds, id]
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Ange ett namn på vyn');
      return;
    }
    if (form.userIds.length === 0) {
      toast.error('Välj minst en behandlare');
      return;
    }
    setSaving(true);
    try {
      // Behåll behandlarordningen från listan
      const ordered = users.map(u => u.user_id).filter(id => form.userIds.includes(id));
      const payload = { name: form.name.trim(), user_ids: ordered };
      if (form.viewId) {
        await updateCalendarView(form.viewId, payload);
        toast.success('Vyn uppdaterad');
      } else {
        await createCalendarView(payload);
        toast.success('Vyn skapad');
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte spara vyn');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (view) => {
    if (!window.confirm(`Ta bort vyn "${view.name}"? Inga bokningar påverkas.`)) return;
    try {
      await deleteCalendarView(view.view_id);
      toast.success('Vyn borttagen');
      if (form?.viewId === view.view_id) setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte ta bort vyn');
    }
  };

  return (
    <section className="settings-section calendar-views-manager">
      <div className="section-header">
        <h2><i className="fas fa-columns"></i> Sparade vyer i dagvyn</h2>
        <p>
          Spara grupper av behandlare, t.ex. "Barn" eller "Fysio". Alla på kliniken kan
          välja vyn i dagvyn – då visas exakt de behandlarna.
        </p>
      </div>

      {loading ? (
        <div className="cvm-empty">Laddar…</div>
      ) : (
        <>
          {views.length === 0 && !form && (
            <div className="cvm-empty">Inga sparade vyer ännu.</div>
          )}
          {views.length > 0 && (
            <ul className="cvm-list">
              {views.map(v => (
                <li key={v.view_id} className="cvm-row">
                  <div className="cvm-info">
                    <span className="cvm-name">{v.name}</span>
                    <span className="cvm-users">
                      {v.user_ids.map(userName).filter(Boolean).join(', ') || 'Inga behandlare med kalender'}
                    </span>
                  </div>
                  <div className="cvm-actions">
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setForm({ viewId: v.view_id, name: v.name, userIds: [...v.user_ids] })}
                    >
                      <i className="fas fa-edit"></i> Ändra
                    </button>
                    <button type="button" className="link-btn cvm-delete" onClick={() => handleDelete(v)}>
                      <i className="fas fa-trash"></i> Ta bort
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {form ? (
            <form className="cvm-form" onSubmit={handleSave}>
              <label className="cvm-label" htmlFor="cvm-name">Namn</label>
              <input
                id="cvm-name"
                type="text"
                maxLength={100}
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="T.ex. Barn"
                autoFocus
              />
              <div className="cvm-label">Behandlare</div>
              <div className="cvm-user-grid">
                {users.map(u => (
                  <label key={u.user_id} className="cvm-user">
                    <input
                      type="checkbox"
                      checked={form.userIds.includes(u.user_id)}
                      onChange={() => toggleUser(u.user_id)}
                    />
                    <span>{u.first_name} {u.last_name}</span>
                  </label>
                ))}
              </div>
              <div className="cvm-form-actions">
                <button type="button" className="link-btn" onClick={() => setForm(null)} disabled={saving}>
                  Avbryt
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Sparar…' : (form.viewId ? 'Spara ändringar' : 'Skapa vy')}
                </button>
              </div>
            </form>
          ) : (
            <button type="button" className="link-btn cvm-add" onClick={() => setForm(EMPTY_FORM)}>
              <i className="fas fa-plus"></i> Ny vy
            </button>
          )}
        </>
      )}
    </section>
  );
}

export default CalendarViewsManager;
